import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { productionService } from '@services/productionService';
import { ProductionEmployeeFormData } from '@/types/production';

/**
 * Formulário de colaborador de produção para criação e edição
 */
export const EmployeeFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ProductionEmployeeFormData>({
    name: '',
    notes: '',
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      loadEmployee(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const loadEmployee = async (employeeId: string) => {
    try {
      setLoading(true);
      setError(null);

      const employee = await productionService.getEmployee(parseInt(employeeId));

      setFormData({
        name: employee.name,
        notes: employee.notes || '',
        is_active: employee.is_active !== false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar colaborador');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof ProductionEmployeeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length > 150) {
      newErrors.name = 'Nome deve ter no máximo 150 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const payload: ProductionEmployeeFormData = {
        name: formData.name.trim(),
        notes: formData.notes?.trim() || undefined,
        is_active: formData.is_active,
      };

      if (isEditing && id) {
        await productionService.updateEmployee(parseInt(id), payload);
      } else {
        await productionService.createEmployee(payload);
      }

      navigate('/production/employees');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar colaborador');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/employees')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/production/employees')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Colaborador' : 'Novo Colaborador'}</h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere as informações do colaborador'
              : 'Preencha os dados para cadastrar um novo colaborador de produção'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <div className="grid gap-6">
        <Card>
          <CardHeader title="Informações Básicas" />
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Nome *"
                placeholder="Ex: Maria da Silva"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                error={errors.name}
                maxLength={150}
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Observações / Notas
                </label>
                <textarea
                  className="input w-full min-h-[100px] resize-y"
                  placeholder="Informações adicionais sobre o colaborador..."
                  value={formData.notes || ''}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Status" />
          <CardContent>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active !== false}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-slate-300"
              />
              <label htmlFor="is_active" className="text-sm text-slate-700">
                Colaborador ativo
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/production/employees')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
            {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Colaborador'}
          </Button>
        </div>
      </div>
    </div>
  );
};
